<template>
  <div id="CategoryPage">
    <div
      v-if="category === null && categoryWithoutInchExist"
      class="back-to-all-categories p-8"
    >
      <div> {{ $tv('backToAllCategoriesText', 'categories') }} </div>
      <nuxt-link
        :to="allCategoriesPageUrl"
        class="k-button k-button-md k-button-solid k-button-solid-primary"
      >
        <span>{{ $tv('backToAllCategoriesBtn', 'categories') }} </span>
      </nuxt-link>
    </div>
    <div v-if="category">
      <div class="header category-header">
        <div class="category-header-and-button">
          <h1
            class="h1--underline"
            v-text="category.Name"
          />
          <div v-if="buttonUrl !== ''">
            <a
              :href="getButtonHref"
              target="_blank"
            >
              <ReactiveButton :button-text="buttonText" />
            </a>
          </div>
        </div>
      </div>
      <div class="category-page__informations">
        <div class="category-page__images-description">
          <div class="category-page__category-images">
            <div>
              <div>
                <img
                  :src="imageUrl(category)"
                  :alt="$tv('designsketch', 'product')"
                  class="designsketch-img"
                >
              </div>
              <div v-if="installationImageUrl(category) !== fallBackImg">
                <img
                  :src="installationImageUrl(category)"
                  :alt="$tv('installationsketch', 'product')"
                  class="installationsketch-img"
                >
              </div>
            </div>
          </div>
          <div
            class="category-page__shortened-description"
            :class="{ 'category-page__shortened-description--expanded': showMoreOpened }"
            ref="categoryDescriptionContainer"
          >
            <h2
              class="h4"
              v-if="category.Description"
            >
              {{ $tv('description', 'categories') }}
            </h2>
            <Markdown
              :text="categoryDescriptionTrim"
              class="header__subheadline header__subheadline--element"
              ref="categoryDescription"
              v-if="category.Description"
            />
            <div
              class="category-page__show-more-button"
              v-show="category && category.Description && exceedsCharacterLimit"
            >
              <ReactiveButton
                :button-text="moreLessButtonText"
                :is-primary-button="false"
                @click="showMoreOpened = !showMoreOpened"
              />
            </div>
          </div>
        </div>
        <div class="category-page__tabs">
          <TabStrip
            v-if="tabs && tabs.length > 0"
            :tabs="tabs"
            class="category-page__tabstrip-container tabstrip"
            :selected="selected"
            @select="onSelect"
          >
            <template #advantages>
              <Markdown
                :text="category.Advantages"
                class="header__subheadline"
              />
            </template>
            <template #downloads>
              <Download :files="categoryDownloads" />
            </template>
            <template #applications>
              <div>
                <div
                  class="applications-headline"
                  v-text="$tv('applicationsHeadline', 'categories')"
                />
                <div class="header__applications">
                  <div
                    v-for="(application, index) in formattedApplicationList"
                    :key="index"
                  >
                    <span
                      class="header__applications__item"
                    > {{ $tv(application, 'applications') }}</span>
                  </div>
                </div>
              </div>
            </template>
            <template #custom="{props}">
              <Markdown
                :text="categoryContents?.Tab[props.dataItem.index]?.TabContent"
                class="header__subheadline"
              />
            </template>
          </TabStrip>
        </div>
      </div>
      <CategoryList
        :categories="category.Subcategories"
        :path="path"
        @applyFilters="handleApplyFilter"
        v-show="category.Subcategories && category.Subcategories.length > 0"
      />
      <div
        class="filter--desktop"
        :class="{ 'filter--mobile': isMobile && openFilter }"
        v-if="!category.IsTopCategory"
      >
        <div
          class="filter-container"
          v-if="!category.IsTopCategory"
        >
          <span
            v-show="isMobile && openFilter"
            class="filter-close-icon"
          >
            <a
              href="javascript:void(0)"
              class="close"
              @click="openFilter = false"
            >
              <NavigationClose />
            </a>
          </span>
          <CategoryFilter
            v-if="categoryFilters && (!(products?.count === 0 && !hasActiveFilters) || products?.count > 0) && products?.facets"
            ref="categoryFilter"
            :filters="filters"
            :facets="products.facets"
            :product-count="products.count"
            :is-mobile="isMobile"
            :is-inch-assortment-category="isInchAssortmentCategory"
            @applyFilters="handleApplyFilter"
            @filterBuyableProducts="loadProducts"
            @filterInchAssortmentOnly="saveInchAssortmentOnly"
          />
          <FilterSkeleton v-else-if="firstLoad" />
        </div>
      </div>
      <div
        class="button-container"
        v-if="isMobile && !category.IsTopCategory && ((filters && filters.length > 0) || $auth.loggedIn || isInchAssortmentCategory)"
      >
        <kbutton
          :theme-color="'primary'"
          @click="openFilter = true"
        >
          <span class="subline">
            {{ $tv('filter', 'categories') }}
          </span>
        </kbutton>
        <kbutton
          :disabled="!activeFilters"
          :theme-color="'base'"
          @click="resetFilters"
        >
          <span class="subline">{{ $tv('reset-filter', 'categories') }}</span>
        </kbutton>
      </div>
      <div
        class="product-table"
        v-if="!category.IsTopCategory"
      >
        <NoProductsFound
          :products="products"
          :has-active-values="hasActiveFilters"
          :form-data="formData"
          :reset-in-progress="resetInProgress"
          @contact-click="handleContactClick"
          @reset-filters="resetFilters"
          @clear-form-data="clearFormData"
        />
        <ProductTable
          v-if="categoryFilters && products?.count>0 && !firstLoad"
          :is-loading="productsLoading"
          :columns="categoryFilters"
          :products="getMappedProductsLocal(products)"
          :sort="tableOptions.sort"
          :pagination="tableOptions.pagination"
          :total="products.count"
          :prices="prices"
          :loading-indicators="loadingIndicators"
          :availabilities="availabilities"
          :successor-availabilities="successorAvailabilities"
          :show-design-sketch="category.Subcategories && category.Subcategories.length > 0"
          :is-default-customer="isDefaultCustomer"
          @favoriteItem="setItemAsFavorite"
          @shopItem="addItemToBasket"
          @sortByColumn="sortItems"
          @changePage="changePageLocal"
          @filterInchValues="loadProducts"
          ref="productTable"
        />
        <ProductTableSkeleton v-else-if="firstLoad" />
        <div
          v-show="categoryContents?.SEOText"
          class="seo-content"
        >
          <Markdown
            :text="categoryContents?.SEOText"
          />
        </div>
      </div>
      <div
        class="footer"
        v-if="category.SEOContentBox"
      >
        <div class="pure-g">
          <div class="footer__content pure-u-1 pure-u-md-2-3">
            <Markdown
              :text="category.SEOContentBox.SEOBoxSmallHeadline"
              class="footer__content__subheader"
            />
            <div
              v-if="category.SEOContentBox"
              class="h1 footer__content__header"
              v-text="category.SEOContentBox.SEOBoxHeadline"
            />
            <Markdown
              :text="category.SEOContentBox.SEOBoxContent"
              class="footer__content__content"
            />
          </div>
          <div class="pure-u-1 pure-u-md-1-3">
            <ResponsiveImage
              :picture="category.SEOContentBox.SEOBoxImages[0]"
              class="footer__image"
            />
          </div>
        </div>
      </div>
      <PopupBase
        :show="openCartModal"
        @close-pop-up="closeCartModal"
        :css-class="buyboxClass"
        v-if="!category.IsTopCategory"
      >
        <ProductBuyBoxPopup
          v-if="(!$auth.loggedIn || ($auth.loggedIn && isDefaultCustomer)) && openCartModal"
          :is-default-customer="isDefaultCustomer"
          :product="product"
        />
        <ProductBuyBox
          v-if="$auth.loggedIn && !isDefaultCustomer && openCartModal"
          :product-data="product"
          :is-default-customer="isDefaultCustomer"
          :loading="reloadProductsInProgress"
          @productSuccessfullyAdded="onProductSuccessfullyAdded"
          @reloadProduct="reloadProduct"
          class="buy-box-in-popup"
        />
      </PopupBase>
    </div>
  </div>
</template>

<script setup>
import ProductBuyBox from '~/components/product-detail/ProductBuyBox.vue'
import ProductBuyBoxPopup from '~/components/product-detail/ProductBuyBoxPopup.vue'
import {useCategoryDesignService} from '@/composables/category_design_service'
import {useProductService} from '~/composables/product_service'
import {
  cleanupFacets,
  cleanupFilters,
  SHOW_INCH_VALUES_FILTER
} from '~/assets/js/filters'
import {
  CATEGORIES,
  NON_ATTRIBUTE_FILTERS,
  CANONICAL_LINK_REGEXP,
  REGION_KEYS,
  PROPERTY_SCOPE_NAMES,
  ASSET_TYPES,
  FALLBACK_IMAGE,
  FORM_DEFAULT_DATA,
  NON_ATTRIBUTE_FILTERS_NEW
} from '~/assets/js/constants'
import {storeToRefs} from 'pinia'
import {getUrlFromTechnicalName, getTechnicalNameFromUrl} from '~/mixins/category_urls'
import searchResultFilters from '~/assets/js/filters/search-result-filters'
import {TabStrip} from '@progress/kendo-vue-layout'
import FilterSkeleton from '~/components/skeletons/FilterSkeleton.vue'
import ProductTableSkeleton from '~/components/skeletons/ProductTableSkeleton.vue'
import {getMappedProductNew, mapProductAttributeNames, getMappedProducts} from '~/assets/js/utils/product_attributes_mapping'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import {useUserStore} from '@/store/user'
import {useCartStore} from '@/store/cart'
import ProductTable from '@/components/product-detail/ProductTable'
import ReactiveButton from '~/components/ReactiveButton'
import Download from '~/components/Download'
import CategoryFilter from '~/components/category/CategoryFilter'
import CategoryList from '~/components/category/CategoryList'
import ResponsiveImage from '~/components/responsive-image.vue'
import PopupBase from '~/components/popup/PopupBase'
import useBreadcrumb from '@/composables/useBreadcrumb'
import useI18nExtended from '~/composables/usei18nExtended'
import NoProductsFound from '~/components/category/NoProductsFound.vue'
import {useDefaultCustomer} from '~/composables/default_customer'
import {useProductAvailabilityPricesHelper} from '~/composables/product_availabilities_prices_helper'
import {useReloadProduct} from '~/composables/reload_product'
import {useAssetImage} from '~/composables/item_image_url'
import {useCategoryFilters} from '~/composables/category_filters'

const buildRedirectUri = function(route, newUrl) {
  let newRoute = {name: route.name, query: route.query, params: {categorypath: [...route.params.categorypath]}}
  newRoute.params.categorypath[newRoute.params.categorypath.length - 1] = newUrl
  return newRoute
}

const productsLoading = ref(false)
const selected = ref(0)

const {
  getCategoryWithDesign,
  getCategoryContentsByUrl,
  getCategoryContentsByCategoryName
} = useCategoryDesignService()
const route = useRoute()
const params = route.params
const i18n = useI18nExtended()
const nuxtApp = useNuxtApp()
const {isDefaultCustomer} = useDefaultCustomer()
const {showInchValues} = storeToRefs(useUserStore())

// product related
let product = null
const products = ref(null)
const {reloadProduct, reloadProductsInProgress} = useReloadProduct(product)
const {prices, availabilities, successorAvailabilities, loadingIndicators} = useProductAvailabilityPricesHelper(products)

// category related
const categories = ref([])
const categoryFilter = ref(null)
const {imageUrl, installationImageUrl} = useAssetImage(categories)

let currentLevel
currentLevel = params.categorypath[params.categorypath.length - 1]

let categoryContents = nuxtApp.payload?.data[`category-contents/url_${currentLevel}_${i18n.locale.value}`]
let category = nuxtApp.payload?.data[`categories/designs_null_0_-1_sortIndex_false_[{"field":"TechnicalName","value":"${categoryContents?.Category?.TechnicalName ?? currentLevel}"},{"field":"Region","value":"${nuxtApp.$globalization.getRegion()}"}]_false`]
let currentCustomerUuid

let formData = FORM_DEFAULT_DATA.CONTACT_FORM
let path = ''
let facets = null
// let currentCategory = null
let openCartModal = ref(false)
// let loggedInStatus = true
let showMoreOpened = false
let categoryTrigger = 0
let firstLoad = true
let openFilter = false
let showInchAssortmentOnly = false
let fallBackImg = FALLBACK_IMAGE
let resetInProgress = false
let productDebouncer = null

productsLoading.value = true // from fetch

const loadProducts = async () => {
  if (category?.IsTopCategory) {
    return
  }
  if (productDebouncer) {
    clearTimeout(productDebouncer)
  }
  productDebouncer = setTimeout(async () => {
    if (process.client) {
      const loadingTimeout = setTimeout(() => { productsLoading.value = true }, 200)
      if (!facets && Array.isArray(categoryFilters)) {
        facets = categoryFilters.filter(
          cat => cat.Attribute.IndexPath !== null
        && cat.Attribute.IndexPath !== ''
        && cat.Attribute.IsFilterable
        && cat.Attribute.TechnicalName.toLowerCase() !== NON_ATTRIBUTE_FILTERS.SHORE_HARDNESS.toLowerCase()
        && cat.Attribute.TechnicalName.toLowerCase() !== NON_ATTRIBUTE_FILTERS.POLYMER.toLowerCase()
        && cat.Attribute.TechnicalName.toLowerCase() !== NON_ATTRIBUTE_FILTERS.MATERIALIDENTIFIER.toLowerCase()
        && cat.Attribute.TechnicalName.toLowerCase() !== NON_ATTRIBUTE_FILTERS.BRAND.toLowerCase()
        && cat.Attribute.TechnicalName.toLowerCase() !== NON_ATTRIBUTE_FILTERS.DESIGN.toLowerCase())
          .map(cat => cat.Attribute.IndexPath)
        facets = cleanupFacets(facets)
        facets.push(NON_ATTRIBUTE_FILTERS.SHORE_HARDNESS)
        facets.push(NON_ATTRIBUTE_FILTERS.POLYMER)
        facets.push(NON_ATTRIBUTE_FILTERS.DESIGN)
        facets.push(NON_ATTRIBUTE_FILTERS.MATERIALIDENTIFIER)
        facets.push(NON_ATTRIBUTE_FILTERS.BRAND)
      }
      if (category && category.designs) {
        const enhanceQueryFilters = [...filters.value]
        let designs = showInchAssortmentOnly ? category.designs.filter(d => d.IsInchCategory) : category.designs
        products.value = await useProductService().getProductsForDesignsNew(
          category.IsTopCategory && !showInchAssortmentOnly ? category.TechnicalName : designs,
          facets,
          nuxtApp.$globalization.getRegion(),
          i18n.locale.value,
          enhanceQueryFilters,
          tableOptions.value,
          showInchValues.value ? [SHOW_INCH_VALUES_FILTER.customQuery] : null,
          PROPERTY_SCOPE_NAMES.CATEGORY_PAGE_NEW,
          categoryAttributeSelects,
          showInchAssortmentOnly
        )
      }
      productsLoading.value = false
      if (firstLoad) {
        firstLoad = false
      }
      if (loadingTimeout) {
        clearTimeout(loadingTimeout)
      }
    }
  }, 250)
}
const {tableOptions, changePage, sortItems, filters, filter, hasActiveFilters} = useCategoryFilters(() => loadProducts(), categoryFilter)

const changePageLocal = (event) => {
  changePage(event)
}

const closeCartModal = () => {
  openCartModal.value = false
}

selected.value = 0
if (category && category.length > 0) {
  category = category[0]
}
if (!categoryContents) {
  categoryContents = await getCategoryContentsByUrl(currentLevel, i18n.locale.value)
  if (!categoryContents && /.*-$/.test(currentLevel)) { // check for tailing hypen
    const sanitizedUrl = currentLevel.substring(0, currentLevel.length - 1)
    categoryContents = await getCategoryContentsByUrl(sanitizedUrl, i18n.locale.value)
  }

  if (!categoryContents) {
    categoryContents = await getCategoryContentsByCategoryName([currentLevel], i18n.locale.value)
    categoryContents = categoryContents && categoryContents.length > 0 ? categoryContents[0] : null
  }
}

if (categoryContents && categoryContents.SEO?.Url && categoryContents.SEO?.Url !== currentLevel) {
  const fullRoute = route
  const newRoute = buildRedirectUri(fullRoute, categoryContents.SEO?.Url)
  await navigateTo(newRoute)
}

let technicalName = null

if (categoryContents) {
  technicalName = categoryContents.Category.TechnicalName

  if (!category) {
    const res = await getCategoryWithDesign(technicalName) ?? {}
    if (res && res.length > 0) {
      category = res[0]
    }
  }
}

let categoryFilters, categoryAttributeSelects
let categoryWithoutInchExist = false
if (!category) {
  throw createError({statusCode: 404, message: 'Category not found name: ' + currentLevel})
} else {
  categoryFilters = category.CategoryAttributes
  if (categoryFilters && category && !category.IsTopCategory) {
    // eslint-disable-next-line eqeqeq
    categoryFilters = categoryFilters.filter(cf => cf.Attribute && 'IndexPath' in cf.Attribute && cf.Attribute.IndexPath != null && cf.Attribute.IndexPath !== '')
    categoryFilters = categoryFilters.map(cf => ({...cf, IndexPath: mapProductAttributeNames(cf.IndexPath)}))
    categoryAttributeSelects = categoryFilters.map(f => f.Attribute.IndexPath)
  }
  category = {
    ...category,
    ...categoryContents
  }
  let defaultFilters = searchResultFilters.filter(f =>
    f.Attribute.TechnicalName.toLowerCase() === NON_ATTRIBUTE_FILTERS.SHORE_HARDNESS.toLowerCase() ||
      f.Attribute.TechnicalName.toLowerCase() === NON_ATTRIBUTE_FILTERS_NEW.PRICING_FLAG.toLowerCase() ||
      f.Attribute.TechnicalName.toLowerCase() === NON_ATTRIBUTE_FILTERS.POLYMER.toLowerCase()
  ).filter(df => typeof (df.Attribute.IndexPath) === 'undefined' || !categoryFilters.some(cf => df.Attribute.IndexPath === cf.Attribute.IndexPath))

  defaultFilters.forEach(filter => {
    categoryFilters.push(filter)
  })

  filters.value = cleanupFilters(categoryFilters)
}
let translatedParams = {}
for (let url of params.categorypath) {
  let foundTechnicalName = url === currentLevel ? technicalName : getTechnicalNameFromUrl(url, i18n.locale.value)
  let currentContents = nuxtApp.payload?.data[`category-contents/url_${url}_${i18n.locale.value}`]
  if (!currentContents) {
    currentContents = await getCategoryContentsByUrl(url, i18n.locale.value)
  }
  for (let localeCode of i18n.localeCodes.value) {
    if (localeCode === i18n.locale.value) {
      continue
    }
    let localizedUrl = currentContents?.localizations.some(l => l.locale === localeCode) ? currentContents?.localizations.find(l => l.locale === localeCode).Url : getUrlFromTechnicalName(foundTechnicalName, localeCode)
    translatedParams[localeCode] = translatedParams[localeCode] || {categorypath: []}
    translatedParams[localeCode].categorypath.push(localizedUrl)
  }
}
route.meta.nuxtI18n = translatedParams

const checkIfCategoryHasDownloads = (category) => {
  const defaultLang = i18n.defaultLocale || 'en'
  const fallbackLocaleCategory = category.CategoryContents?.find(c => c.locale === defaultLang)

  return (category?.Assets && category.Assets.length > 0 ||
      category?.Downloads && category.Downloads.length > 0 ||
      fallbackLocaleCategory?.Assets && fallbackLocaleCategory.Assets.length > 0 ||
      fallbackLocaleCategory?.Downloads && fallbackLocaleCategory.Downloads.length > 0)
}

const tabs = []
if (category?.Advantages) {
  tabs.push({
    title: i18n.tv('advantages', 'categories'),
    content: 'advantages'
  })
}
if (checkIfCategoryHasDownloads(category)) {
  tabs.push({
    title: i18n.tv('downloads', 'categories'),
    content: 'downloads'
  })
}
if (category?.applications && category.applications.length > 0) {
  tabs.push({
    title: i18n.tv('applications', 'categories'),
    content: 'applications'
  })
}

// TODO readd
categoryContents?.Tab?.forEach((tab, index) => {
  if (tab?.TabContent) {
    tabs.push({
      title: tab.TabTitle,
      content: 'custom',
      index: index
    })
  }
})

// head
const router = useRouter()
const regions = category?.regions ?? []
const routePath = route.path
let i18nHead

if (regions.some(r => r.RegionShortName === REGION_KEYS.EU)) {
  const canonicalPath = routePath.match(CANONICAL_LINK_REGEXP)
  if (Array.isArray(canonicalPath) && canonicalPath.length === 3) {
    i18nHead = useLocaleHead({addSeoAttributes: true, canonicalPath: '/' + REGION_KEYS.EU.toLowerCase() + '/' + canonicalPath[2], route: route, router: router, i18n: i18n}).value
  } else {
    i18nHead = useLocaleHead({addSeoAttributes: true, canonicalPath: path, route: route, router: router, i18n: i18n}).value
  }
} else {
  i18nHead = useLocaleHead({addSeoAttributes: true, canonicalPath: path, route: route, router: router, i18n: i18n}).value
}

const defaultTitle = `${(category && category.Name) || 'Categories'} | Freudenberg FST`

let description = category?.SEO && category?.SEO?.Description ? category.SEO.Description : i18n.tv({messageKey: 'categoryDescription', messageValues: {name: category?.Name || 'Categories'}}, 'seo')
if (description !== category?.SEO?.Description) {
  const formattedApplicationList = category?.applications.map(a => a.ApplicationName).sort() ?? []
  description += formattedApplicationList.join(', ')
  description += '.'
}
const head = {
  title: category?.SEO?.Title ?? defaultTitle,
  categoryLanguage: i18n.locale,
  htmlAttrs: {
    ...i18nHead.htmlAttrs
  },
  meta: [
    ...i18nHead.meta,
    {
      hid: 'description',
      name: 'description',
      content: description
    }
  ],
  link: [...i18nHead.link]
}

useHead(head)

const pagePath = route.params.categorypath.map(p => getTechnicalNameFromUrl(p, i18n.locale.value))
let breadcrumbArray = []
breadcrumbArray.push({label: i18n.te('breadcrumbs.category', i18n.locale.value) ? i18n.tv('category', 'breadcrumbs') : 'category', path: `${CATEGORIES.RootCategoriesUrl}`})
breadcrumbArray = breadcrumbArray
  .concat(pagePath.map((p, i) => ({label: i18n.t('categories.' + p), path: `${CATEGORIES.RootCategoriesUrl}/` + pagePath.map(pp => getUrlFromTechnicalName(pp, i18n.locale.value)).slice(0, i + 1).join('/')})))
useBreadcrumb(breadcrumbArray)

const {loadedCustomerUuid} = storeToRefs(useUserStore())
const {deliveryAddressUuid} = storeToRefs(useCartStore())

categories.value = technicalName
currentCustomerUuid = loadedCustomerUuid

onMounted(async () => {
  triggerComputedProp()
  loadProducts()
// const {allDesignAssets} = storeToRefs(useProductDataStore()) TODO doesnt have export anymore
// return {
//   category,
//   categoryFilters,
//   categoryAttributeSelects,
//   filters,
//   categoryWithoutInchExist,
//   tabs,
//   showInchValues,
// categories = technicalName
// currentCustomerUuid = loadedCustomerUuid
//   accessToken,
//   deliveryAddressUuid,
// assets = allDesignAssets
//   categoryContents
// }
})

const pathCategoryPath = route.params.categorypath
path = `${CATEGORIES.RootCategoriesUrl}/` + pathCategoryPath.join('/')

const getMappedProductsLocal = (products) => {
  return getMappedProducts(products)
}

const setItemAsFavorite = (e) => {
  // eslint-disable-next-line no-console
  console.log('setItemAsFavorite', e)
}

const addItemToBasket = (e, productLocal) => {
  product = getMappedProductNew(productLocal)
  openCartModal.value = true
}

const onProductSuccessfullyAdded = () => {
  openCartModal.value = false
}

const saveInchAssortmentOnly = (inchAssortmentOnly) => {
  showInchAssortmentOnly = inchAssortmentOnly
  loadProducts()
}

const onSelect = (event) => {
  return (selected.value = event.selected)
}

const triggerComputedProp = () => {
  if (category && typeof (window) !== 'undefined') {
    const markdownContainer = window.document.getElementsByClassName('header__subheadline--element')
    if (!markdownContainer) {
      setTimeout(triggerComputedProp, 100)
    } else {
      categoryTrigger++
    }
  }
}

const handleApplyFilter = (filters) => {
  filter(filters)
  openFilter = false
}

const clearFormData = () => {
  formData = FORM_DEFAULT_DATA.CONTACT_FORM
}

const buyboxClass = computed(() => {
  return 'popup__buy-box-new'
  // TODO remove computed method
  // return this.$route.query.newpdp === '1' || this.experimentalMode ? 'popup__buy-box-new' : 'popup__buy-box'
})

const categoryDescriptionTrim = computed(() => {
  return category?.Description?.trim() ?? ''
})

const categoryDownloads = computed(() => {
  let result = []
  if (category?.Assets && category.Assets.length > 0) {
    result = category.Assets.map(
      (categoryAsset) => {
        return {
          assetType: categoryAsset.AssetType,
          lookupKey: categoryAsset.AssetURI,
          altText: categoryAsset.AssetAlt,
          sourceCMS: true
        }
      }
    )
  }
  else if (category?.Downloads && category.Downloads.length > 0) {
    result = category.Downloads.map(
      (categoryDownload) => ({
        assetType: categoryDownload.AssetType,
        lookupKey: categoryDownload.File.url,
        altText: categoryDownload.AlternativeText,
        label: categoryDownload.AssetType === ASSET_TYPES.ProductDataSheet ? i18n.tv('ProductDataSheet', 'product.assetLink', categoryDownload.Label) : categoryDownload.Label,
        sourceCMS: true
      })
    )
  } else {
    const defaultLang = i18n.defaultLocale || 'en'
    const fallbackLocaleCategory = category.CategoryContents?.find(c => c.locale === defaultLang)

    if (fallbackLocaleCategory?.Assets && fallbackLocaleCategory.Assets.length > 0) {
      result = fallbackLocaleCategory.Assets.map(
        (categoryAsset) => {
          return {
            assetType: categoryAsset.AssetType,
            lookupKey: categoryAsset.AssetURI,
            altText: categoryAsset.AssetAlt,
            sourceCMS: true
          }
        }
      )
    } else if (fallbackLocaleCategory?.Downloads && fallbackLocaleCategory.Downloads.length > 0) {
      result = fallbackLocaleCategory.Downloads.map(
        (categoryDownload) => ({
          assetType: categoryDownload.AssetType,
          lookupKey: categoryDownload.File.url,
          altText: categoryDownload.AlternativeText,
          label: categoryDownload.AssetType === ASSET_TYPES.ProductDataSheet ? i18n.tv('ProductDataSheet', 'product.assetLink', categoryDownload.Label) : categoryDownload.Label,
          sourceCMS: true
        })
      )
    }
  }
  return result
})

const activeFilters = computed(() => {
  const active = filters.value.filter(f => f.isActive)
  return active && Array.isArray(active) && active.length > 0
})

const exceedsCharacterLimit = computed(() => {
  if (categoryTrigger && category && typeof (window) !== 'undefined') {
    const markdownContainer = window.document.getElementsByClassName('header__subheadline--element')[0]
    return markdownContainer && markdownContainer.scrollHeight > markdownContainer.clientHeight + 16
  }

  return false
})

const moreLessButtonText = computed(() => {
  if (!showMoreOpened) {
    return i18n.tv('show-more-description', 'categories')
  } else {
    return i18n.tv('show-less-description', 'categories')
  }
})

const buttonUrl = computed(() => {
  return category?.ButtonUrl ?? ''
})

const buttonText = computed(() => {
  return category?.ButtonText ?? ''
})

// const getButtonHref = computed(() => {
//   if (URL_HTTP_REGEXP_CHECK.test(this.buttonUrl)) {
//     return this.buttonUrl
//   } else {
//     return this.localePath(this.buttonUrl)
//   }
// })

const formattedApplicationList = computed(() => {
  return category?.applications.map(a => a.ApplicationName).sort() ?? []
})

const isInchAssortmentCategory = computed(() => {
  return category?.IsInchCategory
})

const allCategoriesPageUrl = computed(() => {
  return this.localePath(CATEGORIES.RootCategoriesUrl)
})

watch(deliveryAddressUuid, () => {
  loadProducts()
})

watch(currentCustomerUuid, () => {
  loadProducts()
})

watch(category, () => {
  loadProducts()
})
</script>

<style lang="scss">
.not-found {
  .header__subheadline {
    font-weight: $base-text-font-weight-light;
  }
}

@include breakpoint-down($lg) {
  .category-page__images-description {
    flex-direction: column;
  }
}

@include breakpoint-up($lg) {
  .category-page__shortened-description {
    .h4 {
      margin-top: 0;
      margin-bottom: 1rem;
    }

    p {
      margin-top: 0;
    }

    padding-bottom: 2.5rem;

    .category-page__show-more-button {
      position: absolute;
      bottom: 0;
    }
  }
}

.category-header {
  padding: .2rem $default-margin-padding-2x 1rem;

  @include breakpoint-down($md) {
    padding: $default-margin-padding-width;
  }
}

.category-page {
  &__informations,
  &__tabstrip-container {
    background-color: $color__content--background__secondary;
  }

  &__images-description {
    display: flex;
    justify-content: space-between;
    padding: $default-margin-padding-2x $default-margin-padding-2x 1.875rem;
  }

  &__downloads {
    font-size: 1.2rem;
    padding-top: 1rem;

    .downloads__element--text {
      margin-left: 0;
      padding: .5em;
    }
  }

  &__category-images {
    img {
      display: block;
      width: 100%;
      max-height: 9.8125rem;

      &.designsketch-img {
        max-width: 9.8125rem;
      }

      &.installationsketch-img {
        max-width: 15.5625rem;
      }
    }

    >div {
      display: flex;
      justify-content: flex-start;

      >div {
        display: inline-block;
        background-color: $color__white;
        box-shadow: 0 .1875rem .375rem $color--card__shadow;
        margin-right: $default-gap;
        min-height: 9.8125rem;
        min-width: 9.8125rem;
      }
    }
  }

  &__shortened-description {
    display: flex;
    flex: 1 0;
    flex-direction: column;
    overflow: hidden;
    position: relative;

    .header__subheadline {
      transition: all $transition-time__default ease-in-out;
      max-height: 3.45em;
      -webkit-line-clamp: 3;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin-bottom: .75rem;
    }

    &--expanded .header__subheadline {
      max-height: 50em;
      -webkit-line-clamp: unset;
    }
  }

  &__tabstrip-container {
    margin-top: $default-margin-padding-width;

    @include breakpoint-up($md) {
      margin: 0 $default-margin-padding-2x;
      padding-bottom: 1rem;
    }

    .k-content .k-animation-container {
      display: block;
    }

    .k-tabstrip-items {
      .k-link {
        padding: .625rem 4.375rem;
        font-size: map-get($base-text-button, font-size);
        font-weight: map-get($base-text-button, font-weight);
        background-color: $color__content--background__primary;

        @include breakpoint-down($md) {
          padding: .5rem 2rem;
        }
      }

      .k-item {
        margin-right: .5rem;
        padding-bottom: .25rem;
      }
    }
  }
}

[id='CategoryPage'] {
  padding-bottom: 0;

  > :has(.product-table) {
    .product-table {
      padding-bottom: 12.5rem;
    }
  }

  > :not(:has(.product-table)) {
    .category-list {
      padding-bottom: 12.5rem;
    }
  }

  h1 {
    margin-top: 0;
  }

  .category-header-and-button {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    a {
      color: transparent;
    }
  }

  @include breakpoint-down($md) {
    .category-header-and-button {
      flex-direction: column;
      justify-content: unset;

      >div {
        margin-top: 1rem;
      }
    }
  }

  .header__subheadline {
    font-weight: $base-text-font-weight-light;
    font-size: $base-text-font-size;

    ul {
      li {
        list-style-type: disc;
      }
    }
  }

  .applications-headline {
    font-weight: $base-text-font-weight-bold;
    color: $color__primary;
  }

  .header__applications {
    padding-top: 1rem;
    line-height: 2rem;
    display: flex;
    flex-wrap: wrap;

    .h4 {
      width: 100%;
      margin-bottom: 1rem;
    }

    &__item {
      padding: .3rem;
      margin: .1rem;
      background-color: $color__content--background__primary;
      border: .05rem $color--input__border__secondary--hover solid;
      border-radius: .2rem;
      box-shadow: 0 .02rem .02rem $color--card__shadow;
    }
  }

  .button-container {
    display: flex;
    justify-content: space-between;
    flex-flow: row wrap;
    margin: $default-margin-padding-2x .9375rem 0 .9375rem;

    .k-button {
      margin-bottom: $default-margin-padding-width;
    }
  }

  .product-table {
    position: relative;
    min-height: 15.625rem;
    padding-top: .5rem;

    .skeleton__table {
      margin-left: 1rem;
      margin-right: 1rem;
    }

    @include breakpoint-up($md) {
      background-color: $color__content--background__secondary;
      padding-top: $default-margin-padding-2x;

      .skeleton__table {
        margin-left: 2rem;
        margin-right: 2rem;
      }
    }
  }

  .seo-content {
    padding: $default-margin-padding-2x;
  }

  .footer {
    padding: 4rem $default-margin-padding-2x;

    &__content {
      &__subheader {
        color: $color__primary;
        text-transform: uppercase;
        margin-bottom: 1em;
      }

      &__header {
        font-size: 2em;
        margin-bottom: 1em;
      }

      &__content {
        line-height: 1.25em;
        font-weight: lighter;
        white-space: pre-line;
      }
    }

    &__image {
      display: block;
      margin-top: 2.5rem;
      margin-left: $default-margin-padding-width;

      @include breakpoint-down($md) {
        margin-left: 0;
      }
    }
  }

  .back-to-all-categories .k-button {
    margin-top: 1rem;
  }
}

.filter-close-icon {
  position: absolute;
  right: .625rem;
  top: .625rem;
  font-size: 1.5625rem;
  z-index: 2000;

  .close {
    color: $color__black;
  }
}

body:has(.filter--mobile) {
  overflow: hidden;
}

.k-animation-container {
  z-index: 2000 !important;
}
</style>
